import { defineNuxtPlugin } from '#imports'
import { i18nPlugin } from '~/plugins/vue-plugins/i18n'

import createVuetifyPlugin from '~/plugins/vue-plugins/vuetify'
import type { I18n } from 'vue-i18n'
import { useI18n } from 'vue-i18n'
import directives from '~/plugins/vue-plugins/directives'
import { ConfirmDialogPlugin } from './vue-plugins/use-dialog'
import { AppFetchErrorHandlerPlugin } from './vue-plugins/fetch-error-handler'
import type { createApp } from 'vue'
import { Notify } from '~/lib/notify'
import type { Directive } from '@vue/runtime-core'
import type { VueScanOptions } from 'z-vue-scan'
import { pinia } from '~/plugins/vue-plugins/pinia'
import { isTest } from '~/helper/nuxt'

const HIGHLIGHT_COMPONENT_UPDATES = import.meta.dev && false

export default defineNuxtPlugin({
  name: 'vue-plugins',
  async setup({ vueApp }) {
    const app = vueApp as ReturnType<typeof createApp>

    /**
     * Border flash for component updates - https://github.com/zcf0508/vue-scan
     */
    if (HIGHLIGHT_COMPONENT_UPDATES) {
      const VueScan = await import('z-vue-scan')
      app.use<VueScanOptions>(VueScan.default, {
        displayDuration: 600,
      })
    }

    /**
     * Directives
     */
    for (const [name, directive] of Object.entries(directives)) {
      app.directive(name, directive as Directive)
    }

    /**
     * Global App State management (Store)
     */
    app.use(pinia)

    /**
     * I18n/Translations
     */
    app.use(i18nPlugin)

    /**
     * Components/Design framework
     */
    app.use(createVuetifyPlugin(i18nPlugin as I18n, useI18n))

    /**
     * $confirm dialog helper
     */
    app.use(ConfirmDialogPlugin, i18nPlugin.global.t, {})

    /**
     * Fetch error handler
     */
    app.use(AppFetchErrorHandlerPlugin, (message, type) => {
      Notify(message, { type })
    })

    return
    if (import.meta.dev && !isTest) {
      const [{ createDebugComponentNamesMixin }] = await Promise.all([
        import('~/composables/DebugComponentNamesMixin'),
        import('~/assets/scss/dev.scss'),
      ])
      app.mixin(createDebugComponentNamesMixin())
    }
  },
})
